import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { authService } from '../../../services';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useClientUIContext } from './context/ClientByIdUIContext';
import TelephoneSection from './forms/sections/TelephoneSection';
import { PhoneSVG } from '../../../components/svg';
import TelephoneModalContent from '../../../components/modal/content/TelephoneModalContent';
import GeneralModal from '../../../components/modal/GeneralModal';
import { getColor } from '../../../helpers/getColor';
import { palette } from '../../../styles/theme';
import CustomAlert from '../../../components/Alert';

export default function ClientTelephoneById () {
    const { t } = useTranslation();
    const { id } = useParams();
    const clientId = parseInt(id);
    const clientUIContext = useClientUIContext();
    const clientUIProps = useMemo(() => {
        return {
            isLoading: clientUIContext?.isLoading,
            setIsLoading: clientUIContext?.setIsLoading,
            clientGeneral: clientUIContext?.clientGeneral,
            setClientGeneral: clientUIContext?.setClientGeneral,
            clientTelephone: clientUIContext?.clientTelephone,
            setClientTelephone: clientUIContext?.setClientTelephone,
            clientTabData: clientUIContext.clientTabData,
            setClientTabData: clientUIContext.setClientTabData,
        };
    }, [clientUIContext]);

    const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
    const [sessionUser, setSessionUser] = useState(null);
    const [telephoneFieldValue, setTelephoneFieldValue] = useState('');
    // Alert states

    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });

    const checkTelephoneField = () => {
        if (telephoneFieldValue === '' || !telephoneFieldValue) return true;
        if (!/[0-9]{10}/.test(telephoneFieldValue)) return true;
        if (telephoneFieldValue === clientUIProps?.clientTabData?.person?.telephone.description.replace('+549', '')) return true;
        return false;
    };

    const handleModifyTelephone = async () => {
        clientUIProps.setIsLoading(true);

        const idPerson =
            clientUIProps.clientTelephone?.[0]?.metadata?.id_person ||
            clientUIProps.clientTelephone?.[0]?.id_person ||
            clientUIProps.clientTabData?.person?.id ||
            clientUIProps.clientTabData?.person?.id_person ||
            clientUIProps.clientTabData?.id_person ||
            clientId;

        const data = {
            telephone: '+549' + telephoneFieldValue,
            idUser: clientId,
            idPerson,
            idUserBO: sessionUser?.id,
        };

        try {
            const response = await authService.verifyNewTelephone(data);
            if (response.error) throw new Error(response.error);
            if (response) {
                setAlert({
                    isOpen: true,
                    severity: 'info',
                    title: t('telephone.alert.info.title'),
                    description: t('telephone.alert.info.description'),
                    className: 'clientsMC_Alert--info'
                });
            }
        }
        catch (error) {
            setAlert({
                isOpen: true,
                severity: 'warning',
                title: `${t('telephone.alert.warning.title1')} ${telephoneFieldValue} ${t('telephone.alert.warning.title2')}`,
                description: t('telephone.alert.warning.description'),
                className: 'clientsMC_Alert--warning-light'
            });
        }
        finally {
            clientUIProps.setIsLoading(false);
            setIsModifyModalOpen(!isModifyModalOpen);
        }
    };

    const { clientTabData, setIsLoading, setClientTelephone } = clientUIProps;

    const fetchTelephonesById = useCallback(() => {
        setIsLoading(true);
        setClientTelephone([]);
        try {
            if (!clientTabData || !clientTabData.person) {
                return;
            }
            const telephonesResponse = clientTabData.person.telephones || [];

            if (telephonesResponse.length) {
                const parsedTelephones = telephonesResponse.filter(
                    telephone => telephone.id_telephone_type === 1 || telephone.type?.id === 1
                );
                setClientTelephone(parsedTelephones);
            }
        }
        catch (error) {
            setAlert({
                isOpen: true,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error'
            });
            setOpenAlert(true);
        }
        finally {
            setIsLoading(false);
        }
    }, [clientTabData, setIsLoading, setClientTelephone, t]);

    useEffect(() => {
        fetchTelephonesById();
    }, [fetchTelephonesById]);

    useEffect(() => {
        const storageSessionUser = localStorage.getItem('sessionUser');
        setSessionUser(storageSessionUser ? JSON.parse(storageSessionUser) : null);
    }, [clientId]);

    return (
        <>

            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />

            <Box style={{ backgroundColor: '#FFF', boxShadow: '0px 0px 30px 0px rgba(82, 63, 105, 0.06)' }} width={'100%'} marginTop={'1rem'} padding={'1rem'} >
                <Box gap={'1rem'}>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Box display='flex' gap={'8px'}>
                            {<PhoneSVG customStroke={palette.primary[700]} />}
                            <Typography variant='h5'>{t('telephone.title')}</Typography>
                        </Box>
                        {<Button
                            className='buttonAction'
                            onClick={() => setIsModifyModalOpen(!isModifyModalOpen)}
                            disabled={clientUIProps.isLoading}
                        >
                            {clientUIProps.isLoading ? <CircularProgress size={24} color="inherit" /> : t('telephone.actions')}
                        </Button>}
                    </Box>
                    { <GeneralModal
                        mainButton={{
                            isDisabled: clientUIProps.isLoading || checkTelephoneField(),
                            onMainButtonClick: handleModifyTelephone

                        }}
                        UIProps={clientUIProps}
                        title={'telephone.actions'}
                        width='460px'
                        height='auto'
                        isOpen={isModifyModalOpen}
                        onClose={() => setIsModifyModalOpen(!isModifyModalOpen)}
                        icon={<PhoneSVG customStroke={getColor('primary', 'main')} />}
                        secondaryButton={{
                            onSecondaryButtonClick: () => setIsModifyModalOpen(!isModifyModalOpen),
                            title: 'buttons.cancel'
                        }}
                    >
                        <TelephoneModalContent telephoneField={{ telephoneFieldValue, setTelephoneFieldValue }} />
                    </GeneralModal>}
                    <TelephoneSection telephones={clientUIProps.clientTelephone} isLoading={clientUIProps.isLoading} />
                </Box>
            </Box>
        </>

    );
}
